import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { zipCodeCoordinates } from '../../data/zipCodeCoordinates';
import './MapView.css';

// Set the Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
console.log('[MapView] Mapbox token loaded:', mapboxgl.accessToken ? 'YES (length: ' + mapboxgl.accessToken.length + ')' : 'NO - TOKEN MISSING!');

interface MapData {
  zip: string;
  impressions: number;
  coordinates?: [number, number];
  distance?: number;
  channels?: Array<{
    channel: string;
    impressions: number;
    clicks?: number;
  }>;
  city?: string;
  state?: string;
}

interface DealerLocation {
  dealer_id: number;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface MapViewProps {
  data: {
    display?: MapData[];
    dooh?: MapData[];
    streaming_tv?: MapData[];
  };
  viewLevel?: 'dealer' | 'district' | 'region' | 'corporate';
  dealerLocation?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    coordinates?: [number, number];
  };
  dealerLocations?: DealerLocation[];
  selectedRadius?: number;
  dealerImpressions?: {
    [dealerId: number]: {
      total: number;
      display?: number;
      dooh?: number;
      streaming_tv?: number;
    };
  };
}

const MapView: React.FC<MapViewProps> = ({ data, viewLevel = 'dealer', dealerLocation, dealerLocations, selectedRadius = 30, dealerImpressions }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const dealerMarkers = useRef<mapboxgl.Marker[]>([]);
  const radiusCircleLayerIds = useRef<string[]>([]);
  const radiusCircleSourceIds = useRef<string[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>('display');
  const [zipCodeData, setZipCodeData] = useState<Map<string, number>>(new Map());
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);

  // Process data for selected channel
  useEffect(() => {
    const channelData = data[selectedChannel as keyof typeof data] || [];
    const zipMap = new Map<string, number>();
    
    channelData.forEach((item: MapData) => {
      zipMap.set(item.zip, item.impressions);
    });
    
    setZipCodeData(zipMap);
  }, [data, selectedChannel]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Check if token is available
    if (!mapboxgl.accessToken) {
      console.error('Mapbox token is not set!');
      return;
    }

    // Calculate initial center based on dealer locations
    let initialCenter: [number, number] = [-98.5795, 39.8283]; // Default US center
    let initialZoom = 4;

    if (dealerLocations && dealerLocations.length > 0) {
      // If we have dealer locations, calculate center
      const avgLat = dealerLocations.reduce((sum, loc) => sum + loc.latitude, 0) / dealerLocations.length;
      const avgLng = dealerLocations.reduce((sum, loc) => sum + loc.longitude, 0) / dealerLocations.length;
      initialCenter = [avgLng, avgLat];
      
      // Adjust zoom based on view level and radius for dealer view
      if (viewLevel === 'dealer' && dealerLocations.length === 1) {
        // Adjust zoom based on selected radius
        if (selectedRadius === 10) {
          initialZoom = 12;  // Closer zoom for 10 miles
        } else if (selectedRadius === 20) {
          initialZoom = 11;  // Medium zoom for 20 miles  
        } else {
          initialZoom = 10;  // Wider zoom for 30 miles
        }
      } else if (viewLevel === 'district') {
        initialZoom = 9;
      } else if (viewLevel === 'region') {
        initialZoom = 7;
      } else if (viewLevel === 'corporate') {
        initialZoom = 5;
      }
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: initialCenter,
      zoom: initialZoom,
      projection: 'mercator'
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Set map loaded state
    map.current.on('load', () => {
      console.log('Map loaded successfully');
      setMapLoaded(true);
    });

    // Set style loaded state
    map.current.on('style.load', () => {
      console.log('Map style loaded successfully');
      setStyleLoaded(true);
    });
    
    // Additional style loaded event listener
    map.current.on('styledata', () => {
      if (map.current && map.current.isStyleLoaded()) {
        console.log('Map styledata event - style is loaded');
        setStyleLoaded(true);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [dealerLocations, viewLevel, selectedRadius]);

  // Update markers when data changes or map loads
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      console.log(`MapView: Waiting for map to be ready. mapLoaded: ${mapLoaded}, styleLoaded: ${styleLoaded}`);
      return;
    }
    
    // Use a more reliable check for style loading
    const attemptToAddMarkers = () => {
      if (!map.current || !map.current.isStyleLoaded()) {
        console.log('MapView: Style not ready yet, waiting... StyleLoaded:', map.current?.isStyleLoaded(), 'Loaded:', map.current?.loaded());
        setTimeout(attemptToAddMarkers, 100);
        return;
      }
      
      console.log('MapView: Map and style are ready, adding markers');
      
      try {
        // Add dealer location markers first
        addDealerLocationMarkers();

        // Add radius circles around dealer locations
        addRadiusCircles();

        // Then add advertising data bubbles
        addZipCodeMarkers();
      } catch (error) {
        console.error('Error updating map markers:', error);
      }
    };
    
    // Start the attempt process
    attemptToAddMarkers();

  }, [mapLoaded, zipCodeData, dealerLocations, viewLevel, selectedRadius]); // eslint-disable-line react-hooks/exhaustive-deps

  const addDealerLocationMarkers = () => {
    if (!map.current) return;

    // Remove existing dealer markers
    dealerMarkers.current.forEach(marker => marker.remove());
    dealerMarkers.current = [];

    // Add dealer location markers
    if (dealerLocations && dealerLocations.length > 0) {
      console.log(`Adding ${dealerLocations.length} dealer markers to map`);
      
      dealerLocations.forEach(dealer => {
        // Skip if no valid coordinates
        if (!dealer.latitude || !dealer.longitude) {
          console.warn(`Skipping dealer ${dealer.name} - no coordinates`);
          return;
        }

        // Get impression metrics for this dealer
        const impressions = dealerImpressions?.[dealer.dealer_id];

        // Create popup content with impression metrics
        let popupContent = `
          <div class="dealer-popup">
            <h4>${dealer.name}</h4>
            ${dealer.address ? `<p>${dealer.address}</p>` : ''}
            <p>${dealer.city || ''}, ${dealer.state || ''} ${dealer.zip || ''}</p>
        `;

        // Add impression metrics if available
        if (impressions) {
          popupContent += `
            <div class="dealer-impressions">
              <hr style="margin: 8px 0; border-color: #ddd;" />
              <p style="font-weight: bold; margin: 4px 0;">
                Total Impressions (${selectedRadius} mi): ${impressions.total.toLocaleString()}
              </p>
          `;

          // Add channel breakdown if available
          if (impressions.display || impressions.dooh || impressions.streaming_tv) {
            popupContent += `<div style="font-size: 11px; margin-top: 4px;">`;
            if (impressions.display) popupContent += `<p style="margin: 2px 0;">Display: ${impressions.display.toLocaleString()}</p>`;
            if (impressions.dooh) popupContent += `<p style="margin: 2px 0;">DOOH: ${impressions.dooh.toLocaleString()}</p>`;
            if (impressions.streaming_tv) popupContent += `<p style="margin: 2px 0;">Streaming TV: ${impressions.streaming_tv.toLocaleString()}</p>`;
            popupContent += `</div>`;
          }

          popupContent += `</div>`;
        }

        popupContent += `</div>`;

        const popup = new mapboxgl.Popup({ offset: 25 })
          .setHTML(popupContent);

        // Determine marker color based on view level
        let markerColor = '#DA291C'; // Default Gravely red
        let markerScale = 1.0;
        
        switch (viewLevel) {
          case 'corporate':
            markerColor = '#1E88E5'; // Blue
            markerScale = 0.8;
            break;
          case 'region':
            markerColor = '#43A047'; // Green
            markerScale = 0.9;
            break;
          case 'district':
            markerColor = '#FB8C00'; // Orange
            markerScale = 0.9;
            break;
          case 'dealer':
            markerColor = '#DA291C'; // Red
            markerScale = 1.2;
            break;
        }

        // Create and add marker
        const marker = new mapboxgl.Marker({
          color: markerColor,
          scale: markerScale
        })
          .setLngLat([dealer.longitude, dealer.latitude])
          .setPopup(popup)
          .addTo(map.current!);

        dealerMarkers.current.push(marker);
      });

      // Fit map to show all dealers
      if (dealerLocations.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        dealerLocations.forEach(dealer => {
          if (dealer.latitude && dealer.longitude) {
            bounds.extend([dealer.longitude, dealer.latitude]);
          }
        });

        map.current.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          duration: 1500,
          maxZoom: viewLevel === 'dealer' ? 11 : viewLevel === 'district' ? 10 : viewLevel === 'region' ? 8 : 6
        });
      } else if (dealerLocations.length === 1 && dealerLocations[0].latitude && dealerLocations[0].longitude) {
        // For single dealer, center on it with radius-appropriate zoom
        let dealerZoom = 11; // default
        if (viewLevel === 'dealer') {
          if (selectedRadius === 10) {
            dealerZoom = 12;
          } else if (selectedRadius === 20) {
            dealerZoom = 11;
          } else {
            dealerZoom = 10;
          }
        }
        
        map.current.flyTo({
          center: [dealerLocations[0].longitude, dealerLocations[0].latitude],
          zoom: dealerZoom,
          duration: 1500
        });
      }
    }
  };

  const addZipCodeMarkers = () => {
    if (!map.current) return;

    try {
      // Style is already verified in attemptToAddMarkers - no need to check again
      console.log('MapView: Adding zip code markers');

      // Remove existing source and layer if they exist
      if (map.current.getSource('zip-markers')) {
        if (map.current.getLayer('zip-markers-layer')) {
          map.current.removeLayer('zip-markers-layer');
        }
        map.current.removeSource('zip-markers');
      }

      // Create features for advertising bubbles
      const features: any[] = [];
      
      // Use the map data directly instead of zipCodeData Map
      const channelData = data[selectedChannel as keyof typeof data] || [];
      
      console.log(`MapView: Processing ${channelData.length} items for ${selectedChannel} channel`);
      console.log('MapView: First few items:', channelData.slice(0, 3));
      
      channelData.forEach((item: MapData) => {
        let coords: [number, number] | undefined;
        
        // First try to use coordinates from API response
        if (item.coordinates) {
          coords = item.coordinates;
        } 
        // Fallback to static ZIP code coordinates (for backwards compatibility)
        else {
          coords = zipCodeCoordinates[item.zip];
        }
        
        if (!coords) {
          console.log(`No coordinates found for ZIP: ${item.zip}`);
          return;
        }
        
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coords
          },
          properties: {
            zip: item.zip,
            impressions: item.impressions,
            distance: item.distance || 0,
            size: Math.min(Math.max(20, Math.sqrt(item.impressions) / 10), 60)
          }
        });
      });

      if (features.length === 0) {
        console.log(`MapView: No advertising data to display on map for ${selectedChannel}. Channel data length: ${channelData.length}`);
        console.log('MapView: Available data channels:', Object.keys(data));
        return;
      }
      
      console.log(`MapView: Created ${features.length} map features for ${selectedChannel}`);

      // Add source
      try {
        map.current.addSource('zip-markers', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: features
          }
        });
        
        console.log('MapView: Successfully added zip-markers source');
      } catch (error) {
        console.error('Error adding source to map:', error);
        return;
      }

      // Color based on channel
      const colors: { [key: string]: string } = {
        display: '#FF6B6B',      // Coral red
        dooh: '#4ECDC4',         // Teal
        streaming_tv: '#45B7D1' // Sky blue
      };

      // Add layer
      try {
        map.current.addLayer({
          id: 'zip-markers-layer',
          type: 'circle',
          source: 'zip-markers',
          paint: {
            'circle-radius': ['get', 'size'],
            'circle-color': colors[selectedChannel] || '#666',
            'circle-stroke-color': 'white',
            'circle-stroke-width': 2,
            'circle-opacity': 0.8
          }
        });
      } catch (error) {
        console.error('Error adding layer to map:', error);
        return;
      }

      // Add click handler for popups
      map.current.on('click', 'zip-markers-layer', (e) => {
        if (!e.features || !e.features[0]) return;

        const coordinates = (e.features[0].geometry as any).coordinates.slice();
        const { zip, impressions, city, state } = e.features[0].properties as any;

        // Find the full data for this ZIP to get all channels
        const channelData = data[selectedChannel as keyof typeof data] || [];
        const zipData = channelData.find((item: MapData) => item.zip === zip);

        // Build popup HTML with all channel data
        let popupHTML = `
          <div class="map-popup">
            <h4>ZIP: ${zip}</h4>
            ${city && state ? `<p class="zip-location">${city}, ${state}</p>` : ''}
            <hr style="margin: 8px 0; border-color: #ddd;" />
            <p style="font-weight: bold; margin-bottom: 8px;">
              Total: ${impressions.toLocaleString()} impressions
            </p>
        `;

        // Add channel breakdown if available
        if (zipData && zipData.channels && zipData.channels.length > 0) {
          popupHTML += `<div class="channel-breakdown" style="font-size: 12px;">`;
          zipData.channels.forEach((ch: any) => {
            const channelColors: { [key: string]: string } = {
              'Display': '#FF6B6B',
              'DOOH': '#4ECDC4',
              'Streaming TV': '#45B7D1'
            };
            const color = channelColors[ch.channel] || '#666';
            popupHTML += `
              <div style="margin: 4px 0; display: flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${color}; border-radius: 50%; margin-right: 6px;"></span>
                <span>${ch.channel}: ${ch.impressions.toLocaleString()}</span>
              </div>
            `;
          });
          popupHTML += `</div>`;
        } else {
          // Fallback if no channel data
          popupHTML += `<p class="channel">${selectedChannel.replace('_', ' ').toUpperCase()}</p>`;
        }

        popupHTML += `</div>`;

        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(popupHTML)
          .addTo(map.current!);
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'zip-markers-layer', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      });
      
      map.current.on('mouseleave', 'zip-markers-layer', () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
      });
    } catch (error) {
      console.error('Error adding zip code markers:', error);
    }
  };

  const addRadiusCircles = () => {
    if (!map.current || !map.current.isStyleLoaded()) {
      console.log('MapView: Map not ready for radius circles');
      return;
    }

    console.log('MapView: Adding radius circles, layers:', radiusCircleLayerIds.current.length, 'sources:', radiusCircleSourceIds.current.length);

    // Remove existing radius circle layers FIRST
    radiusCircleLayerIds.current.forEach(layerId => {
      try {
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId);
          console.log('MapView: Removed layer:', layerId);
        }
      } catch (error) {
        console.warn(`Error removing layer ${layerId}:`, error);
      }
    });
    radiusCircleLayerIds.current = [];

    // NOW remove sources (after all layers are removed)
    radiusCircleSourceIds.current.forEach(sourceId => {
      try {
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId);
          console.log('MapView: Removed source:', sourceId);
        }
      } catch (error) {
        console.warn(`Error removing source ${sourceId}:`, error);
      }
    });
    radiusCircleSourceIds.current = [];

    // Only show radius circles for dealer view level
    if (viewLevel !== 'dealer' || !dealerLocations || dealerLocations.length === 0) {
      return;
    }

    console.log(`MapView: Adding radius circles (${selectedRadius} miles) for ${dealerLocations.length} dealer(s)`);

    // Convert miles to meters (1 mile = 1609.34 meters)
    const radiusInMeters = selectedRadius * 1609.34;

    // Create a circle for each dealer location
    dealerLocations.forEach((dealer, index) => {
      if (!dealer.latitude || !dealer.longitude) return;

      // Create circle using Turf.js circle helper (approximated GeoJSON circle)
      const center = [dealer.longitude, dealer.latitude];
      const options = { steps: 64, units: 'meters' as const };

      // Generate circle points manually (simple approximation)
      const circlePoints: number[][] = [];
      const steps = 64;
      const earthRadius = 6371000; // Earth's radius in meters

      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * 2 * Math.PI;

        // Calculate offset in degrees (rough approximation)
        const latOffset = (radiusInMeters / earthRadius) * (180 / Math.PI);
        const lngOffset = (radiusInMeters / (earthRadius * Math.cos(dealer.latitude * Math.PI / 180))) * (180 / Math.PI);

        const lat = dealer.latitude + latOffset * Math.sin(angle);
        const lng = dealer.longitude + lngOffset * Math.cos(angle);

        circlePoints.push([lng, lat]);
      }

      const layerId = `radius-circle-${index}`;
      const sourceId = `radius-circle-${index}`;

      try {
        // Add source
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [circlePoints]
            },
            properties: {
              radius: selectedRadius,
              dealer: dealer.name
            }
          }
        });

        // Add fill layer (semi-transparent)
        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': '#DA291C', // Gravely red
            'fill-opacity': 0.1
          }
        });

        // Add outline layer
        const outlineLayerId = `${layerId}-outline`;
        map.current!.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#DA291C', // Gravely red
            'line-width': 2,
            'line-opacity': 0.6,
            'line-dasharray': [2, 2] // Dashed line
          }
        });

        // Track layers and sources separately
        radiusCircleLayerIds.current.push(layerId, outlineLayerId);
        radiusCircleSourceIds.current.push(sourceId);

        console.log(`MapView: Added radius circle for ${dealer.name} (${selectedRadius} miles)`);
      } catch (error) {
        console.error(`Error adding radius circle for dealer ${dealer.name}:`, error);
      }
    });
  };


  const channels = [
    { key: 'display', label: 'Display', color: '#FF6B6B' },
    { key: 'dooh', label: 'DOOH', color: '#4ECDC4' },
    { key: 'streaming_tv', label: 'Streaming TV', color: '#45B7D1' }
  ];

  return (
    <div className="map-container">
      <div className="map-header">
        <div className="channel-selector">
          {channels.map(channel => (
            <button
              key={channel.key}
              className={`channel-btn ${selectedChannel === channel.key ? 'active' : ''}`}
              onClick={() => setSelectedChannel(channel.key)}
              style={selectedChannel === channel.key ? { 
                backgroundColor: channel.color,
                borderColor: channel.color,
                color: 'white'
              } : {}}
            >
              {channel.label}
            </button>
          ))}
        </div>
      </div>
      <div ref={mapContainer} className="map-box" />
      <div className="map-legend">
        <div className="bubble-size-legend">
          <p style={{ marginBottom: '8px', fontWeight: 500 }}>Impression Volume</p>
          <div className="bubble-scale">
            <div className="bubble-scale-item">
              <div className="scale-bubble" style={{
                width: '15px',
                height: '15px',
                backgroundColor: selectedChannel === 'display' ? '#FF6B6B' :
                                selectedChannel === 'dooh' ? '#4ECDC4' : '#45B7D1'
              }}></div>
              <span>Low</span>
            </div>
            <div className="bubble-scale-item">
              <div className="scale-bubble" style={{
                width: '30px',
                height: '30px',
                backgroundColor: selectedChannel === 'display' ? '#FF6B6B' :
                                selectedChannel === 'dooh' ? '#4ECDC4' : '#45B7D1'
              }}></div>
              <span>Medium</span>
            </div>
            <div className="bubble-scale-item">
              <div className="scale-bubble" style={{
                width: '50px',
                height: '50px',
                backgroundColor: selectedChannel === 'display' ? '#FF6B6B' :
                                selectedChannel === 'dooh' ? '#4ECDC4' : '#45B7D1'
              }}></div>
              <span>High</span>
            </div>
          </div>
        </div>
        {viewLevel === 'dealer' && (
          <p className="radius-info">Showing {selectedRadius}-mile coverage area</p>
        )}
        {dealerMarkers.current.length > 0 && (
          <div className="dealer-legend">
            <span className="dealer-marker" style={{
              backgroundColor: viewLevel === 'dealer' ? '#DA291C' :
                              viewLevel === 'district' ? '#FB8C00' :
                              viewLevel === 'region' ? '#43A047' : '#1E88E5'
            }}></span>
            <span>Dealer location{dealerMarkers.current.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;
